import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { PageHeader } from "~/components/PageHeader";
import { Spinner } from "~/components/ui/spinner";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { addMonths, format, startOfMonth } from "date-fns";
import type { DateRange } from "react-day-picker";
import { CalendarIcon } from "~/icons/CalendarIcon";

type MapsApiUsageSummaryRow = {
  day: string;
  api: string;
  outcome: string;
  cnt: number;
  /** Callsite enum; null on rows written before cost attribution was added. */
  source?: string | null;
  /** Lifecycle stage enum; null on pre-attribution rows. */
  stage?: string | null;
  cacheLayer?: string | null;
};

const MAPS_SOURCE_LABELS: Record<string, string> = {
  search_list: "Suche: Trefferliste",
  search_markers: "Suche: Kartenmarker",
  search_map_center: "Suche: Kartenzentrierung",
  search_bar: "Suchleiste",
  filter_dialog: "Filter-Dialog",
  cms_resolver: "CMS-Link (loc=)",
  location_form: "Location-Formular",
  search_map: "Suche: Karte (Browser)",
  location_form_map: "Location-Formular: Karte (Browser)",
  unknown: "Unbekannt",
};

function formatMapsSourceLabel(source: string | null | undefined): string {
  if (!source) return "– (vor Attribution)";
  return MAPS_SOURCE_LABELS[source] ?? source;
}

const MAPS_STAGE_LABELS: Record<string, string> = {
  cache_hit: "Cache-Treffer",
  cache_miss: "Cache-Fehltreffer",
  deduplicated: "Zusammengefasst",
  google_attempt: "Google: Versuch",
  google_result: "Google: Antwort",
  blocked_budget: "Durch Budget blockiert",
  blocked_rate_limit: "Durch Rate-Limit blockiert",
  blocked_invalid_session: "Ungültige/verbrauchte Session",
  blocked_guard_unavailable: "Schutzsystem nicht verfügbar",
  error: "Fehler",
  client_map_cta_opened: "Browser: Karte geöffnet",
  client_map_mounted: "Browser: Karte geladen",
  client_maps_js_load_ok: "Browser: Maps JS OK",
  client_maps_js_load_error: "Browser: Maps JS Fehler",
};

function formatMapsStageLabel(stage: string | null | undefined): string {
  if (!stage) return "–";
  return MAPS_STAGE_LABELS[stage] ?? stage;
}

function parseLocalYyyyMmDd(value: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return undefined;
  const year = Number.parseInt(m[1], 10);
  const month = Number.parseInt(m[2], 10);
  const day = Number.parseInt(m[3], 10);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }
  const d = new Date(year, month - 1, day);
  if (!Number.isFinite(d.getTime())) return undefined;
  // Reject overflow normalization (e.g. 2026-02-31 -> 2026-03-03).
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return undefined;
  }
  return d;
}

function formatLocalYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export const Route = createFileRoute("/dashboard/maps-api-usage")({
  component: RouteComponent,
});

const numberLocale = "de-DE";

const formatInteger = new Intl.NumberFormat(numberLocale, {
  maximumFractionDigits: 0,
});

const formatPercent = new Intl.NumberFormat(numberLocale, {
  style: "percent",
  maximumFractionDigits: 0,
});

const formatUsd = new Intl.NumberFormat(numberLocale, {
  style: "currency",
  currency: "USD",
});

function configuredSkuRate(raw: unknown, fallback: number): number {
  if (raw == null || (typeof raw === "string" && raw.trim() === "")) {
    return fallback;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

const serverSkuUsdPerThousand: Record<string, number> = {
  geocoding: configuredSkuRate(
    import.meta.env.VITE_MAPS_COST_GEOCODING_PER_1000_USD,
    5,
  ),
  places_autocomplete: configuredSkuRate(
    import.meta.env.VITE_MAPS_COST_AUTOCOMPLETE_PER_1000_USD,
    2.83,
  ),
  place_details: configuredSkuRate(
    import.meta.env.VITE_MAPS_COST_PLACE_DETAILS_PER_1000_USD,
    5,
  ),
};

function estimateServerCostUsd(rows: MapsApiUsageSummaryRow[]): number {
  return rows.reduce((total, row) => {
    if (row.stage !== "google_attempt") return total;
    return total + (row.cnt * (serverSkuUsdPerThousand[row.api] ?? 0)) / 1000;
  }, 0);
}

function formatMapsOutcomeLabel(outcome: string): string {
  switch (outcome) {
    case "cache_hit_memory":
      return "Vermieden (RAM-Cache)";
    case "cache_hit_memory_zero_results":
      return "Vermieden (RAM-Cache, keine Treffer)";
    case "cache_hit_db":
      return "Vermieden (DB-Cache)";
    case "cache_hit_db_zero_results":
      return "Vermieden (DB-Cache, keine Treffer)";
    case "success":
      return "Google: Erfolg";
    case "error":
      return "Google: Fehler";
    case "zero_results":
      return "Google: Keine Treffer";
    case "cache_miss":
      return "Cache-Fehltreffer";
    case "deduplicated":
      return "Vermieden (zusammengefasst)";
    case "google_attempt":
      return "Google: Versuch (ausgehend)";
    case "blocked_budget":
      return "Blockiert (Budget-Limit)";
    case "blocked_rate_limit":
      return "Blockiert (Rate-Limit)";
    case "blocked_invalid_session":
      return "Blockiert (Session ungültig/verbraucht)";
    case "blocked_guard_unavailable":
      return "Blockiert (Schutzsystem nicht verfügbar)";
    case "client_event":
      return "Browser-Ereignis (nicht abrechnungsrelevant)";
    default:
      return outcome;
  }
}

type GeocodeSummary = {
  googleCalls: number;
  avoidedRam: number;
  avoidedDb: number;
  avoidedTotal: number;
  geocodeEventsTotal: number;
  cacheSharePct: number;
  avoidedDeduplicated: number;
  blockedByBudget: number;
};

function summarizeGeocode(rows: MapsApiUsageSummaryRow[]): GeocodeSummary {
  let googleCalls = 0;
  let avoidedRam = 0;
  let avoidedDb = 0;
  let avoidedDeduplicated = 0;
  let blockedByBudget = 0;

  // Budget blocks and dedup hits are counted across all Maps APIs: they are
  // cluster-wide cost controls, not Geocoding-specific.
  for (const r of rows) {
    if (r.outcome === "deduplicated") avoidedDeduplicated += r.cnt;
    if (r.outcome === "blocked_budget") blockedByBudget += r.cnt;
  }

  for (const r of rows) {
    if (r.api !== "geocoding") continue;
    if (r.stage === "google_attempt" || r.outcome === "google_attempt") {
      googleCalls += r.cnt;
      continue;
    }
    switch (r.outcome) {
      case "cache_hit_memory":
      case "cache_hit_memory_zero_results":
        avoidedRam += r.cnt;
        break;
      case "cache_hit_db":
      case "cache_hit_db_zero_results":
        avoidedDb += r.cnt;
        break;
      case "success":
      case "error":
      case "zero_results":
        // Pre-attribution rows have no `google_attempt`; retain compatibility
        // without double-counting modern attempt + result pairs.
        if (!r.stage) googleCalls += r.cnt;
        break;
      default:
        break;
    }
  }

  const avoidedTotal = avoidedRam + avoidedDb;
  const geocodeEventsTotal = googleCalls + avoidedTotal;
  const cacheSharePct =
    geocodeEventsTotal > 0
      ? Math.round((avoidedTotal / geocodeEventsTotal) * 100)
      : 0;

  return {
    googleCalls,
    avoidedRam,
    avoidedDb,
    avoidedTotal,
    geocodeEventsTotal,
    cacheSharePct,
    avoidedDeduplicated,
    blockedByBudget,
  };
}

/** Gradient + default text color shared by RAM card and RAM table rows */
const GEOCODE_RAM_GRADIENT_TEXT =
  "bg-linear-to-br from-violet-600 to-violet-700 text-white";

const GEOCODE_RAM_CARD_CLASSNAME = `rounded-md border border-violet-700 ${GEOCODE_RAM_GRADIENT_TEXT} p-4 shadow-md`;

const GEOCODE_RAM_TABLE_ROW_CLASSNAME = `border-b border-violet-700 ${GEOCODE_RAM_GRADIENT_TEXT} transition-colors hover:brightness-105`;

/** Gradient + default text color shared by DB card and DB table rows */
const GEOCODE_DB_GRADIENT_TEXT =
  "bg-linear-to-br from-cyan-500 to-cyan-600 text-white";

const GEOCODE_DB_CARD_CLASSNAME = `rounded-md border border-cyan-700 ${GEOCODE_DB_GRADIENT_TEXT} p-4 shadow-md`;

const GEOCODE_DB_TABLE_ROW_CLASSNAME = `border-b border-cyan-700 ${GEOCODE_DB_GRADIENT_TEXT} transition-colors hover:brightness-105`;

function mapsRowUsesGeocodeStyle(api: string): boolean {
  return (
    api === "geocoding" ||
    api === "place_details" ||
    api === "places_autocomplete"
  );
}

function geocodeRowClass(api: string, outcome: string): string {
  if (!mapsRowUsesGeocodeStyle(api)) {
    return "border-b text-foreground transition-colors hover:bg-muted/50";
  }
  if (outcome === "cache_hit_memory" || outcome === "cache_hit_memory_zero_results") {
    return GEOCODE_RAM_TABLE_ROW_CLASSNAME;
  }
  if (outcome === "cache_hit_db" || outcome === "cache_hit_db_zero_results") {
    return GEOCODE_DB_TABLE_ROW_CLASSNAME;
  }
  if (
    outcome === "success" ||
    outcome === "error" ||
    outcome === "zero_results"
  ) {
    return "border-b bg-muted/50 text-foreground transition-colors hover:bg-muted/70";
  }
  return "border-b text-foreground transition-colors hover:bg-muted/50";
}

function geocodeTableDataCellClass(api: string, outcome: string): string {
  const base = "p-3 align-middle";
  if (!mapsRowUsesGeocodeStyle(api)) return base;
  if (outcome === "cache_hit_memory" || outcome === "cache_hit_memory_zero_results")
    return `${base} text-violet-100`;
  if (outcome === "cache_hit_db" || outcome === "cache_hit_db_zero_results")
    return `${base} text-cyan-50`;
  return base;
}

function geocodeTableCountCellClass(api: string, outcome: string): string {
  const base = "p-3 align-middle text-right tabular-nums";
  if (
    mapsRowUsesGeocodeStyle(api) &&
    (outcome === "cache_hit_memory" ||
      outcome === "cache_hit_db" ||
      outcome === "cache_hit_memory_zero_results" ||
      outcome === "cache_hit_db_zero_results")
  ) {
    return `${base} font-medium text-white`;
  }
  return base;
}

function RouteComponent() {
  const [range, setRange] = useState<DateRange | undefined>(undefined);

  const {
    data: availableDays,
    isPending: isAvailableDaysPending,
    isError: isAvailableDaysError,
    error: availableDaysError,
  } = useQuery({
    queryKey: ["mapsApiUsageAvailableDays"],
    queryFn: async (): Promise<{
      days: string[];
      minDay: string | null;
      maxDay: string | null;
    }> => {
      const res = await fetch("/api/dashboard/maps-api-usage-available-days", {
        credentials: "include",
      });
      if (res.status === 401 || res.status === 403) {
        throw new Error("MAPS_USAGE_FORBIDDEN");
      }
      if (!res.ok) {
        throw new Error(`HTTP_${res.status}`);
      }
      const body = (await res.json()) as {
        ok: boolean;
        days?: string[];
        minDay?: string | null;
        maxDay?: string | null;
      };
      if (
        !body.ok ||
        !Array.isArray(body.days) ||
        typeof body.minDay === "undefined" ||
        typeof body.maxDay === "undefined"
      ) {
        throw new Error("Invalid response");
      }
      return {
        days: body.days,
        minDay: body.minDay,
        maxDay: body.maxDay,
      };
    },
  });

  const hasAnyAvailableDays = Boolean(availableDays && availableDays.days.length > 0);
  const isAvailableDaysUnavailable =
    isAvailableDaysPending || isAvailableDaysError || !hasAnyAvailableDays;
  const availableDaysHelperText = useMemo(() => {
    if (isAvailableDaysPending) return "Kalender wird geladen…";
    if (isAvailableDaysError) {
      return availableDaysError instanceof Error
        ? availableDaysError.message
        : "Kalender konnte nicht geladen werden.";
    }
    if (!availableDays) return "Kalenderdaten sind nicht verfügbar.";
    if (availableDays.days.length === 0) return "Keine Daten für einen Zeitraum verfügbar.";
    return null;
  }, [isAvailableDaysPending, isAvailableDaysError, availableDaysError, availableDays]);

  const availableDaySet = useMemo(() => {
    return new Set(availableDays?.days ?? []);
  }, [availableDays?.days]);

  const minAvailableDate = useMemo(() => {
    if (!availableDays?.minDay) return undefined;
    return parseLocalYyyyMmDd(availableDays.minDay);
  }, [availableDays?.minDay]);

  const maxAvailableDate = useMemo(() => {
    if (!availableDays?.maxDay) return undefined;
    return parseLocalYyyyMmDd(availableDays.maxDay);
  }, [availableDays?.maxDay]);

  const pickerStartMonth = useMemo(() => {
    return minAvailableDate ? startOfMonth(minAvailableDate) : undefined;
  }, [minAvailableDate]);

  const pickerEndMonth = useMemo(() => {
    if (!maxAvailableDate) return undefined;
    const end = startOfMonth(maxAvailableDate);
    // Ensure at least two months are renderable; otherwise DayPicker may show only one.
    if (pickerStartMonth) {
      const minEnd = addMonths(pickerStartMonth, 1);
      return end < minEnd ? minEnd : end;
    }
    return end;
  }, [maxAvailableDate, pickerStartMonth]);

  const selectedFrom = range?.from;
  const selectedTo = range?.to;
  const hasCompleteRange = Boolean(selectedFrom && selectedTo);

  const {
    data: rows,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [
      "mapsApiUsageSummary",
      hasCompleteRange ? formatLocalYyyyMmDd(selectedFrom!) : null,
      hasCompleteRange ? formatLocalYyyyMmDd(selectedTo!) : null,
    ],
    queryFn: async (): Promise<MapsApiUsageSummaryRow[]> => {
      const url = hasCompleteRange
        ? `/api/dashboard/maps-api-usage-summary?from=${encodeURIComponent(
            formatLocalYyyyMmDd(selectedFrom!),
          )}&to=${encodeURIComponent(formatLocalYyyyMmDd(selectedTo!))}`
        : "/api/dashboard/maps-api-usage-summary?days=30";
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 401 || res.status === 403) {
        throw new Error("MAPS_USAGE_FORBIDDEN");
      }
      if (!res.ok) {
        throw new Error(`HTTP_${res.status}`);
      }
      const body = (await res.json()) as {
        ok: boolean;
        rows?: MapsApiUsageSummaryRow[];
      };
      if (!body.ok || !Array.isArray(body.rows)) {
        throw new Error("Invalid response");
      }
      return body.rows;
    },
  });

  const sorted = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      if (a.day !== b.day) return b.day.localeCompare(a.day);
      if (a.api !== b.api) return a.api.localeCompare(b.api);
      if (a.outcome !== b.outcome) return a.outcome.localeCompare(b.outcome);
      return (a.source ?? "").localeCompare(b.source ?? "");
    });
  }, [rows]);

  const geocodeSummary = useMemo(
    () => (rows ? summarizeGeocode(rows) : null),
    [rows],
  );
  const estimatedCostUsd = useMemo(
    () => estimateServerCostUsd(rows ?? []),
    [rows],
  );

  if (
    isError &&
    error instanceof Error &&
    error.message === "MAPS_USAGE_FORBIDDEN"
  ) {
    throw notFound();
  }

  if (isError) {
    return (
      <div className="flex-1">
        <PageHeader title="Maps API Nutzung" />
        <p className="text-sm text-destructive">
          Daten konnten nicht geladen werden. Bitte später erneut versuchen.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 mb-12">
      <PageHeader title="Maps API Nutzung (Superadmin)" />
      <div className="flex flex-col gap-4 -mt-2 mb-6">
        <p className="text-sm text-foreground/90 max-w-2xl leading-relaxed">
          Geocoding-, Places- und Details-Aktivität. Für Geocoding (und seit
        dem Autocomplete-DB-Cache auch Places Autocomplete) zeigen wir Treffer
        aus RAM- und DB-Cache getrennt von Google-Aufrufen — siehe farbige
        Zeilen und Kasten „Geocoding im Überblick“.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-foreground/80">
            Zeitraum
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                id="date-picker-range"
                className="justify-start px-2.5 font-normal w-[260px]"
                disabled={isAvailableDaysUnavailable}
              >
                <CalendarIcon />
                {range?.from ? (
                  range.to ? (
                    <>
                      {format(range.from, "dd.MM.y")} - {format(range.to, "dd.MM.y")}
                    </>
                  ) : (
                    format(range.from, "dd.MM.y")
                  )
                ) : isAvailableDaysUnavailable ? (
                  <span>Zeitraum nicht verfügbar</span>
                ) : (
                  <span>Zeitraum auswählen</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              {isAvailableDaysUnavailable ? (
                <div className="p-3 text-sm text-foreground/80">
                  {availableDaysHelperText}
                </div>
              ) : (
                <Calendar
                  mode="range"
                  defaultMonth={range?.from ?? maxAvailableDate}
                  selected={range}
                  onSelect={(next) => {
                    setRange(next);
                  }}
                  numberOfMonths={2}
                  disabled={(date) => {
                    const localDay = formatLocalYyyyMmDd(date);
                    return !availableDaySet.has(localDay);
                  }}
                  startMonth={pickerStartMonth}
                  endMonth={pickerEndMonth}
                  captionLayout="dropdown"
                />
              )}
            </PopoverContent>
          </Popover>
          {availableDaysHelperText ? (
            <p className="w-full text-xs text-foreground/70">
              {availableDaysHelperText}
            </p>
          ) : null}
        </div>
      </div>
      {isPending ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Spinner />
          <span>Lade…</span>
        </div>
      ) : (
        <>
          {geocodeSummary ? (
            <section
              className="mb-8 rounded-lg border border-border bg-card p-5 shadow-sm"
              aria-labelledby="maps-cost-controls-heading"
            >
              <h2
                id="maps-cost-controls-heading"
                className="text-base font-semibold tracking-tight text-foreground"
              >
                Kostenbremsen (alle Maps-APIs)
              </h2>
              <p className="mt-2 max-w-3xl text-sm text-foreground/90 leading-relaxed">
                Diese Werte gelten über alle Maps-APIs hinweg. „Blockiert“ ist
                unsere eigene Notbremse und ersetzt <strong>keine</strong>{" "}
                Google-Cloud-Quotas — die bleiben der endgültige harte Stopp.
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <div className="rounded-md border border-border bg-muted p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Vermieden: zusammengefasste Anfragen
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                    {formatInteger.format(geocodeSummary.avoidedDeduplicated)}
                  </p>
                  <p className="mt-1 text-xs text-foreground/85 leading-snug">
                    Gleichzeitig identische Anfragen im selben Worker, die auf
                    denselben Google-Aufruf gewartet haben
                  </p>
                </div>
                <div className="rounded-md border border-border bg-muted p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Geschätzte Server-API-Kosten
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                    {formatUsd.format(estimatedCostUsd)}
                  </p>
                  <p className="mt-1 text-xs text-foreground/85 leading-snug">
                    Aus `google_attempt` und konfigurierbaren SKU-Raten; ohne
                    Dynamic Maps, Freikontingente und Mengenrabatte.
                  </p>
                </div>
                <div className="rounded-md border border-border bg-muted p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Durch Budget blockiert
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                    {formatInteger.format(geocodeSummary.blockedByBudget)}
                  </p>
                  <p className="mt-1 text-xs text-foreground/85 leading-snug">
                    Anfragen, die das Minuten- oder Tageslimit erreicht haben.
                    Dauerhaft &gt; 0 bedeutet: Limits prüfen oder Ursache suchen.
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          {geocodeSummary && geocodeSummary.geocodeEventsTotal > 0 ? (
            <section
              className="mb-8 rounded-lg border border-border bg-card p-5 shadow-sm"
              aria-labelledby="maps-geocode-summary-heading"
            >
              <h2
                id="maps-geocode-summary-heading"
                className="text-base font-semibold tracking-tight text-foreground"
              >
                Geocoding im Überblick
              </h2>
              <p className="mt-2 max-w-3xl text-sm text-foreground/90 leading-relaxed">
                Sinkende Google-Geocoding-Zahlen zusammen mit steigenden
                „Vermieden“-Werten bedeutet: dieselbe Nutzung wird häufiger aus
                dem Cache bedient — das ist die beabsichtigte Entlastung nach
                Einführung von RAM- und Datenbank-Cache.
              </p>
              <p className="mt-3 max-w-3xl rounded-md border border-border bg-muted px-3 py-2.5 text-sm text-foreground leading-relaxed">
                <span className="font-semibold">Hinweis:</span> Der RAM-Cache
                wird immer zuerst geprüft. Nach einem Treffer aus der Datenbank
                landet das Ergebnis im RAM — viele weitere Anfragen erscheinen
                daher als „RAM“, obwohl die Datenbank den ersten Treffer nach
                Ablauf der RAM-Zeit oder auf einer neuen Instanz geliefert hat.
                Die Zeile „Vermieden (DB-Cache)“ in der Tabelle unten zählt
                ausschließlich echte DB-Lesevorgänge.
              </p>

              <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-md border border-border bg-muted p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Bei Google (HTTP)
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                    {formatInteger.format(geocodeSummary.googleCalls)}
                  </p>
                  <p className="mt-1 text-xs text-foreground/85 leading-snug">
                    Geocoding-API, zählt zur Nutzung / Abrechnung bei Google
                  </p>
                </div>
                <div className={GEOCODE_RAM_CARD_CLASSNAME}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-100">
                    Vermieden: RAM-Cache
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-white">
                    {formatInteger.format(geocodeSummary.avoidedRam)}
                  </p>
                  <p className="mt-1 text-xs text-violet-100 leading-snug">
                    In-Memory (ca. 10 Min. pro Prozess)
                  </p>
                </div>
                <div className={GEOCODE_DB_CARD_CLASSNAME}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-cyan-50">
                    Vermieden: DB-Cache
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-white">
                    {formatInteger.format(geocodeSummary.avoidedDb)}
                  </p>
                  <p className="mt-1 text-xs text-cyan-50 leading-snug">
                    Lesevorgänge aus{" "}
                    <code className="rounded bg-white/20 px-1 py-0.5 font-mono text-[0.7rem] text-white">
                      lg_geocode_cache
                    </code>{" "}
                    (ohne Google)
                  </p>
                </div>
                <div className="rounded-md border border-border bg-muted p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Cache-Anteil
                  </p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums text-foreground">
                    {formatPercent.format(geocodeSummary.cacheSharePct / 100)}
                  </p>
                  <p className="mt-1 text-xs text-foreground/85 leading-snug">
                    Ohne Google gesamt:{" "}
                    {formatInteger.format(geocodeSummary.avoidedTotal)} (RAM + DB)
                  </p>
                </div>
              </div>

              {geocodeSummary.avoidedTotal > 0 ? (
                <div className="mt-4">
                  <p className="mb-1.5 text-xs font-medium text-foreground/90">
                    Innerhalb des Caches: RAM (violett) vs. Datenbank (cyan)
                  </p>
                  <div
                    className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
                    role="img"
                    aria-label={`Cache-Aufteilung: ${formatPercent.format(
                      geocodeSummary.avoidedRam / geocodeSummary.avoidedTotal,
                    )} RAM, ${formatPercent.format(
                      geocodeSummary.avoidedDb / geocodeSummary.avoidedTotal,
                    )} Datenbank`}
                  >
                    <div
                      className="h-full bg-violet-500"
                      style={{
                        width: `${(100 * geocodeSummary.avoidedRam) / geocodeSummary.avoidedTotal}%`,
                      }}
                    />
                    <div
                      className="h-full bg-cyan-500"
                      style={{
                        width: `${(100 * geocodeSummary.avoidedDb) / geocodeSummary.avoidedTotal}%`,
                      }}
                    />
                  </div>
                </div>
              ) : null}

              <div className="mt-5">
                <p className="mb-1.5 text-xs font-medium text-foreground/90">
                  Gesamt: Cache (grün) vs. Google (grau)
                </p>
                <div
                  className="flex h-4 w-full overflow-hidden rounded-full bg-muted"
                  role="img"
                  aria-label={`Geocoding: ${formatPercent.format(geocodeSummary.cacheSharePct / 100)} Cache, ${formatPercent.format((100 - geocodeSummary.cacheSharePct) / 100)} Google`}
                >
                  <div
                    className="h-full bg-emerald-500 transition-[width] duration-300"
                    style={{
                      width: `${geocodeSummary.cacheSharePct}%`,
                    }}
                  />
                  <div
                    className="h-full bg-slate-400/55"
                    style={{
                      width: `${100 - geocodeSummary.cacheSharePct}%`,
                    }}
                  />
                </div>
              </div>
            </section>
          ) : null}

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm caption-bottom">
              <thead className="border-b border-border bg-muted/50 [&_tr]:border-b">
                <tr className="border-b transition-colors">
                  <th className="h-10 px-3 text-left align-middle font-medium">
                    Tag
                  </th>
                  <th className="h-10 px-3 text-left align-middle font-medium">
                    API
                  </th>
                  <th className="h-10 px-3 text-left align-middle font-medium">
                    Ergebnis
                  </th>
                  <th className="h-10 px-3 text-left align-middle font-medium">
                    Quelle
                  </th>
                  <th className="h-10 px-3 text-left align-middle font-medium">
                    Phase
                  </th>
                  <th className="h-10 px-3 text-right align-middle font-medium">
                    Anzahl
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {sorted.length === 0 ? (
                  <tr className="border-b transition-colors">
                    <td
                      colSpan={6}
                      className="p-3 align-middle text-muted-foreground"
                    >
                      Noch keine Einträge in diesem Zeitraum.
                    </td>
                  </tr>
                ) : (
                  sorted.map((r, i) => (
                    <tr
                      key={`${r.day}-${r.api}-${r.outcome}-${r.source ?? ""}-${r.stage ?? ""}-${i}`}
                      className={geocodeRowClass(r.api, r.outcome)}
                    >
                      <td className={geocodeTableDataCellClass(r.api, r.outcome)}>
                        {r.day}
                      </td>
                      <td className={geocodeTableDataCellClass(r.api, r.outcome)}>
                        {r.api}
                      </td>
                      <td className={geocodeTableDataCellClass(r.api, r.outcome)}>
                        {formatMapsOutcomeLabel(r.outcome)}
                      </td>
                      <td className={geocodeTableDataCellClass(r.api, r.outcome)}>
                        {formatMapsSourceLabel(r.source)}
                      </td>
                      <td className={geocodeTableDataCellClass(r.api, r.outcome)}>
                        {formatMapsStageLabel(r.stage)}
                      </td>
                      <td
                        className={geocodeTableCountCellClass(r.api, r.outcome)}
                      >
                        {formatInteger.format(r.cnt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
